import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Radar, Save, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABELS = {
  b2b_prada: "Folge 1 · Prada/ChatGPT",
  b2b_loyalty: "Folge 2 · +28,7% Kundenbindung",
  b2b_anprobe: "Folge 3 · Anprobe",
};

const Spark = ({ series }) => {
  const max = Math.max(1, ...series.map((s) => s.visits));
  return (
    <div className="flex h-10 items-end gap-[3px]" title="Besuche der letzten 14 Tage">
      {series.map((s) => (
        <div key={s.day} title={`${s.day}: ${s.visits}`}
          className="w-2 rounded-sm"
          style={{ height: `${Math.max(8, (s.visits / max) * 100)}%`, background: s.visits > 0 ? "var(--camel)" : "var(--line)" }} />
      ))}
    </div>
  );
};

export const BriefingRadar = ({ adminKey }) => {
  const [items, setItems] = useState(null);
  const [echo, setEcho] = useState({});
  const [saving, setSaving] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/admin/ads/briefing`, { headers: { "X-Admin-Key": adminKey } })
      .then((r) => {
        setItems(r.data.items || []);
        const e = {};
        (r.data.items || []).forEach((it) => { e[it.campaign] = { reactions: it.echo.reactions ?? "", comments: it.echo.comments ?? "", impressions: it.echo.impressions ?? "" }; });
        setEcho(e);
      })
      .catch(() => setItems([]));
  }, [adminKey]);

  useEffect(load, [load]);

  const saveEcho = async (campaign) => {
    setSaving(campaign);
    try {
      await axios.post(`${API}/admin/ads/briefing/echo`, { campaign, ...echo[campaign] }, { headers: { "X-Admin-Key": adminKey } });
    } catch { /* noop */ }
    setSaving("");
  };

  if (!items) return null;
  const active = items.filter((it) => it.visits > 0 || it.first_day);

  return (
    <div className="mt-10" data-testid="briefing-radar">
      <div className="flex items-center gap-2">
        <Radar size={18} style={{ color: "var(--camel)" }} />
        <h3 className="font-serif-display text-lg font-semibold">Briefing-Radar · LinkedIn-Serie</h3>
      </div>
      <p className="mt-1 text-xs" style={{ color: "var(--muted-warm)" }}>
        Pro Folge: UTM-Besuche (automatisch) plus Kommentar-Echo von LinkedIn (manuell eintragen: Impressionen, Reaktionen, Kommentare aus der Post-Statistik). «Erste 3 Tage» zeigt den Serien-Effekt direkt nach dem Posten.
      </p>
      {active.length === 0 ? (
        <div className="mt-4 rounded-2xl border p-6 text-center text-sm" style={{ borderColor: "var(--line)", color: "var(--muted-warm)" }} data-testid="briefing-radar-empty">
          Noch keine Briefing-Besuche getrackt. Sobald jemand über den Erstkommentar-Link (z.B. utm_campaign=b2b_prada) kommt, erscheint die Folge hier.
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {active.map((it) => (
            <div key={it.campaign} className="rounded-2xl border p-5" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)" }} data-testid={`briefing-card-${it.campaign}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold">{LABELS[it.campaign] || it.campaign}</p>
                  <p className="text-[11px]" style={{ color: "var(--muted-warm)" }}>{it.campaign}{it.first_day ? ` · erster Besuch ${it.first_day}` : ""}</p>
                </div>
                <Spark series={it.series} />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                {[["Besuche", it.visits, `briefing-visits-${it.campaign}`], ["Erste 3 Tage", it.day3_visits, `briefing-day3-${it.campaign}`], ["Leads", it.leads, `briefing-leads-${it.campaign}`]].map(([l, v, tid]) => (
                  <div key={tid} className="rounded-xl border px-2 py-2.5" style={{ borderColor: "var(--line)", background: "var(--surface)" }}>
                    <p className="text-[10px] font-bold uppercase" style={{ letterSpacing: "0.07em", color: "var(--muted-warm)" }}>{l}</p>
                    <p className="font-serif-display text-xl font-semibold" data-testid={tid}>{v}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap items-end gap-2">
                {[["impressions", "Impressionen"], ["reactions", "Reaktionen"], ["comments", "Kommentare"]].map(([k, l]) => (
                  <label key={k} className="text-[10px] font-bold uppercase" style={{ letterSpacing: "0.07em", color: "var(--muted-warm)" }}>
                    {l}
                    <input type="number" min="0" value={echo[it.campaign]?.[k] ?? ""}
                      data-testid={`briefing-echo-${k}-${it.campaign}`}
                      onChange={(e) => setEcho((p) => ({ ...p, [it.campaign]: { ...p[it.campaign], [k]: e.target.value } }))}
                      className="mt-1 block w-24 rounded-lg border px-2 py-1.5 text-sm font-normal normal-case"
                      style={{ borderColor: "var(--line)", background: "var(--surface)", letterSpacing: "normal" }} />
                  </label>
                ))}
                <button onClick={() => saveEcho(it.campaign)} disabled={saving === it.campaign}
                  data-testid={`briefing-echo-save-${it.campaign}`}
                  className="flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold text-white disabled:opacity-60"
                  style={{ background: "var(--camel)" }}>
                  {saving === it.campaign ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  Echo speichern
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
